import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlobalNavSectionType } from "@/types/common/library";

const STORAGE_VERSION = "v3";

type CollapsedMap = Record<string, boolean>;

function makeStorageKey(scopeKey: string) {
  return `nav:${STORAGE_VERSION}:${scopeKey}:collapsedSections`;
}

function safeReadStorage(scopeKey: string): CollapsedMap {
  if (typeof window === "undefined") return {};
  try {
    const stored = localStorage.getItem(makeStorageKey(scopeKey));
    return stored ? (JSON.parse(stored) as CollapsedMap) : {};
  } catch (error) {
    console.error("Failed to load collapsed sections. Keeping defaults.", error);
    return {};
  }
}

function safeWriteStorage(scopeKey: string, value: CollapsedMap) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(makeStorageKey(scopeKey), JSON.stringify(value));
  } catch (error) {
    console.error("Failed to store collapsed sections", error);
  }
}

export function useCollapsibleNavSections(menuSections: GlobalNavSectionType[], scopeKey: string) {
  const sectionKeyFor = useCallback((section: GlobalNavSectionType) => {
    return (section.name || "").trim();
  }, []);

  const explicitSectionFromUrl = useCallback((): string | undefined => {
    if (typeof window === "undefined") return undefined;
    try {
      const url = new URL(window.location.href);
      const querySec = url.searchParams.get("sec") || undefined;
      const hash = url.hash?.replace(/^#/, "") || undefined;
      return querySec || hash || undefined;
    } catch (error) {
      console.error("Failed to parse URL", error);
      return undefined;
    }
  }, []);

  const matchExplicitToSection = useCallback(
    (candidate: string | undefined) => {
      if (!candidate) return undefined;
      const slug = candidate.toLowerCase().replace(/\s+/g, "-");
      const target = menuSections.find((s) => {
        const name = (s.name || "").trim();
        if (!name) return false;
        const nameSlug = name.toLowerCase().replace(/\s+/g, "-");
        return name === candidate || nameSlug === slug;
      });
      return target ? sectionKeyFor(target) : undefined;
    },
    [menuSections, sectionKeyFor]
  );

  // Server-safe initial state: collapse all sections by default.
  // localStorage/URL-based state is applied in an effect after hydration.
  const computeServerSafe = useCallback((): CollapsedMap => {
    const initial: CollapsedMap = {};
    menuSections
      .filter((section) => section.position !== "bottom" && !!section.name?.trim())
      .forEach((section) => {
        const key = sectionKeyFor(section);
        const hasActive = section.items?.some((it) => it.isActive);
        initial[key] = !hasActive; // expand active sections, collapse others
      });
    return initial;
  }, [menuSections, sectionKeyFor]);

  const [collapsedSections, setCollapsedSections] = useState<CollapsedMap>(computeServerSafe);

  // After hydration, sync from localStorage and URL
  const hasHydrated = useRef(false);
  useEffect(() => {
    if (hasHydrated.current) return;
    hasHydrated.current = true;

    const stored = safeReadStorage(scopeKey);
    const explicit = matchExplicitToSection(explicitSectionFromUrl());

    setCollapsedSections((prev) => {
      const next: CollapsedMap = { ...prev };

      // Apply stored preferences
      for (const [key, value] of Object.entries(stored)) {
        next[key] = value;
      }

      // Ensure active sections are expanded
      menuSections
        .filter((section) => section.position !== "bottom" && !!section.name?.trim())
        .forEach((section) => {
          const key = sectionKeyFor(section);
          const hasActive = section.items?.some((it) => it.isActive);
          if (hasActive) {
            next[key] = false;
          }
        });

      // URL-specified section takes priority
      if (explicit) {
        next[explicit] = false;
      }

      return next;
    });
  }, [menuSections, scopeKey, explicitSectionFromUrl, matchExplicitToSection, sectionKeyFor]);

  // Handle new sections appearing in menuSections
  useEffect(() => {
    setCollapsedSections((prev) => {
      const next: CollapsedMap = { ...prev };
      menuSections
        .filter((section) => section.position !== "bottom" && !!section.name?.trim())
        .forEach((section) => {
          const key = sectionKeyFor(section);
          if (typeof next[key] === "undefined") {
            next[key] = true;
          }
        });
      return next;
    });
  }, [menuSections, sectionKeyFor]);

  const activeSectionKey = useMemo(() => {
    const sec = menuSections.find((section) => section.items?.some((it) => it.isActive));
    return sec ? sectionKeyFor(sec) : undefined;
  }, [menuSections, sectionKeyFor]);

  const prevActiveRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!activeSectionKey) return;
    if (prevActiveRef.current === activeSectionKey) return;
    setCollapsedSections((prev) => ({ ...prev, [activeSectionKey]: false }));
    prevActiveRef.current = activeSectionKey;
  }, [activeSectionKey]);

  const toggleSection = useCallback(
    (name: string) => {
      setCollapsedSections((prev) => {
        const updated = { ...prev, [name]: !prev[name] };
        safeWriteStorage(scopeKey, updated);
        return updated;
      });
    },
    [scopeKey]
  );

  const hasHeader = useCallback((section: GlobalNavSectionType) => !!section.name?.trim(), []);

  const isCollapsed = useCallback(
    (section: GlobalNavSectionType) => {
      if (!hasHeader(section)) return false;
      const key = sectionKeyFor(section);
      if (key in collapsedSections) return !!collapsedSections[key];
      return true;
    },
    [collapsedSections, hasHeader, sectionKeyFor]
  );

  return {
    hasHeader,
    isCollapsed,
    toggleSection,
  };
}

export default useCollapsibleNavSections;
